import { createReducer } from '@reduxjs/toolkit';

import type {
	SvaraDailyCompletionDto,
	SvaraDailyMissionDto,
	SvaraMissionClaimGrant,
} from '../../services/api';
import { loginSuccess, logout } from '../auth/authActions';
import {
	missionChestClaimRequest,
	missionClaimDismiss,
	missionClaimFailure,
	missionClaimRequest,
	missionClaimSuccess,
	MissionClaimTarget,
	MISSIONS_CHEST_TARGET,
	missionsHubFailure,
	missionsHubRequest,
	missionsHubSuccess,
	missionsModalClosed,
	missionsModalOpened,
} from './missionsActions';

export type MissionsState = {
	cycleKey: string | null;
	resetAt: string | null;
	missions: SvaraDailyMissionDto[];
	completion: SvaraDailyCompletionDto | null;

	hubLoading: boolean;
	hubErrorKey?: string;

	modalOpen: boolean;

	claimingTargets: Record<string, true>;
	lastClaim: {
		target: MissionClaimTarget;
		outcome: 'paid' | 'partial' | 'alreadyClaimed';
		grants: SvaraMissionClaimGrant[];
	} | null;
	claimError: { target: MissionClaimTarget; key: string } | null;
};

const initial: MissionsState = {
	cycleKey: null,
	resetAt: null,
	missions: [],
	completion: null,
	hubLoading: false,
	hubErrorKey: undefined,
	modalOpen: false,
	claimingTargets: {},
	lastClaim: null,
	claimError: null,
};

const beginClaim = (s: MissionsState, target: MissionClaimTarget) => {
	s.claimingTargets[target] = true;
	s.lastClaim = null;
	s.claimError = null;
};

export const missionsReducer = createReducer(initial, b => {
	b.addCase(missionsHubRequest, s => {
		s.hubLoading = true;
		s.hubErrorKey = undefined;
	});
	b.addCase(missionsHubSuccess, (s, a) => {
		s.hubLoading = false;
		s.cycleKey = a.payload.cycleKey;
		s.resetAt = a.payload.resetAt;
		s.missions = a.payload.missions ?? [];
		s.completion = a.payload.completion ?? null;

		const claimedTargets = new Set<string>(
			(a.payload.missions ?? []).filter(mission => mission.claimed).map(mission => mission.id),
		);
		if (a.payload.completion?.claimed) claimedTargets.add(MISSIONS_CHEST_TARGET);

		if (s.claimError && claimedTargets.has(s.claimError.target)) s.claimError = null;
		if (s.lastClaim?.outcome === 'alreadyClaimed' && claimedTargets.has(s.lastClaim.target)) {
			s.lastClaim = null;
		}
	});
	b.addCase(missionsHubFailure, (s, a) => {
		s.hubLoading = false;
		s.hubErrorKey = a.payload;
	});

	b.addCase(missionClaimRequest, (s, a) => {
		beginClaim(s, a.payload.missionId);
	});
	b.addCase(missionChestClaimRequest, s => {
		beginClaim(s, MISSIONS_CHEST_TARGET);
	});
	b.addCase(missionClaimSuccess, (s, a) => {
		delete s.claimingTargets[a.payload.target];
		s.lastClaim = a.payload;
		s.claimError = null;
	});
	b.addCase(missionClaimFailure, (s, a) => {
		delete s.claimingTargets[a.payload.target];
		s.claimError = a.payload;
		s.lastClaim = null;
	});
	b.addCase(missionClaimDismiss, s => {
		s.lastClaim = null;
		s.claimError = null;
	});

	b.addCase(missionsModalOpened, s => {
		s.modalOpen = true;
	});
	b.addCase(missionsModalClosed, s => {
		s.modalOpen = false;
		s.lastClaim = null;
		s.claimError = null;
	});

	b.addCase(logout, () => initial);
	b.addCase(loginSuccess, () => initial);
});

type WithMissions = { missions: MissionsState };

export const selectMissionsModalOpen = (s: WithMissions) => s.missions.modalOpen;
export const selectMissionsCycleKey = (s: WithMissions) => s.missions.cycleKey;
export const selectMissionsResetAt = (s: WithMissions) => s.missions.resetAt;
export const selectMissionsList = (s: WithMissions) => s.missions.missions;
export const selectMissionsCompletion = (s: WithMissions) => s.missions.completion;
export const selectMissionsHubLoading = (s: WithMissions) => s.missions.hubLoading;
export const selectMissionsHubErrorKey = (s: WithMissions) => s.missions.hubErrorKey;
export const selectMissionsClaimingTargets = (s: WithMissions) => s.missions.claimingTargets;
export const selectMissionsLastClaim = (s: WithMissions) => s.missions.lastClaim;
export const selectMissionsClaimError = (s: WithMissions) => s.missions.claimError;

export const selectMissionsTotalCount = (s: WithMissions) => s.missions.missions.length;
export const selectMissionsCompletedCount = (s: WithMissions) =>
	s.missions.missions.reduce((count, mission) => count + (mission.completed ? 1 : 0), 0);
